import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import { usePollingQuery } from '@/hooks/usePollingQuery'
import type { EnableAutoRefillResponse, PaymentMethod, Plan } from '@/services/api/api.types'

// Auto-refill threshold: 10% of bundle size (shown to users but not editable yet)
const AUTO_REFILL_THRESHOLD_PERCENTAGE = 0.1

interface UseAutoRefillConfigProps {
  plans: Plan[]
  currentPlan?: Plan
  paymentMethod?: PaymentMethod | null
  currentAutoRefillEnabled?: boolean
  currentThresholdCredits?: number | null
  onPlanUpdate: (planId: string, autoRefill: boolean) => Promise<void>
  onClose: () => void
}

export function useAutoRefillConfig({
  plans,
  currentPlan,
  paymentMethod = null,
  currentAutoRefillEnabled = false,
  currentThresholdCredits = null,
  onPlanUpdate,
  onClose,
}: UseAutoRefillConfigProps) {
  const { api } = useGlobalState()
  const queryClient = useQueryClient()
  const { pollUntilCondition } = usePollingQuery()

  // State management
  const [selectedPlan, setSelectedPlan] = useState('')
  const [autoRefillEnabled, setAutoRefillEnabled] = useState(currentAutoRefillEnabled)
  const [thresholdCredits, setThresholdCredits] = useState<number>(0)
  const [isUpdating, setIsUpdating] = useState(false)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [isPollingAfterPayment, setIsPollingAfterPayment] = useState(false)

  // Derived state
  const selectedPlanData = plans?.find(p => p.bundleId === selectedPlan)
  const hasPaymentMethod = paymentMethod !== null

  // Helper function to calculate threshold
  const calculateThreshold = (creditsPerBundle: number) =>
    Math.floor(creditsPerBundle * AUTO_REFILL_THRESHOLD_PERCENTAGE)

  // Polling strategy to wait for payment method update after payment setup
  const pollForPaymentMethodUpdate = useCallback(async () => {
    return await pollUntilCondition({
      queryKey: ['billing', 'info'],
      condition: (data: any) => {
        const updatedBillingInfo = data?.billingInfo
        const updatedPaymentMethod = updatedBillingInfo?.paymentMethod

        // Payment method is successfully added when it's no longer null/undefined
        return updatedPaymentMethod !== null && updatedPaymentMethod !== undefined
      },
      logContext: 'Auto-refill payment method polling',
    })
  }, [pollUntilCondition])

  // Shared success handler for mutations
  const handleMutationSuccess = async (planId: string, autoRefill: boolean, successMessage: string) => {
    setIsUpdating(true)
    try {
      // Refresh billing info
      queryClient.invalidateQueries({ queryKey: ['billing', 'info'] })
      await onPlanUpdate(planId, autoRefill)

      // Show success notification
      notifications.show({
        title: 'Success',
        message: successMessage,
        color: 'green',
      })

      onClose()
    } catch (error) {
      console.error('Failed to update after auto-refill change:', error)
      notifications.show({
        title: 'Update Failed',
        message: 'Settings updated but there was an issue refreshing the display. Please refresh the page.',
        color: 'orange',
      })
    } finally {
      setIsUpdating(false)
    }
  }

  // Success handler for updateAutoRefill (when payment method exists)
  const handleUpdateAutoRefillSuccess = (_response: EnableAutoRefillResponse) => {
    // Direct completion - no payment flow needed
    handleMutationSuccess(selectedPlan, autoRefillEnabled, 'Auto-refill enabled successfully')
  }

  // Success handler for changePaymentMethod (when no payment method exists)
  const handlePaymentMethodSetupSuccess = (response: { clientSecret: string }) => {
    // Payment flow required - show payment form
    setClientSecret(response.clientSecret)
    setShowPaymentForm(true)
  }

  // Shared error handler for mutations
  const handleApiMutationError = (error: any, context: 'setup' | 'update' | 'payment') => {
    const errorMessages = {
      setup: 'Failed to setup payment method:',
      update: 'Failed to enable auto-refill:',
      payment: 'Failed to setup payment method:',
    }
    console.error(errorMessages[context], error)
    // Error display is handled by component via error state, not notifications
  }

  // Mutation for setting up payment method (when no payment method exists)
  const setupPaymentMethodMutation = useMutation({
    mutationFn: async () => {
      return await api.changePaymentMethod()
    },
    onSuccess: handlePaymentMethodSetupSuccess,
    onError: error => handleApiMutationError(error, 'payment'),
  })

  const updateAutoRefillMutation = useMutation({
    mutationFn: async () => {
      const plan = plans?.find(p => p.bundleId === selectedPlan)
      if (!plan) throw new Error('Plan not found')

      return await api.updateAutoRefill({
        bundle_id: plan.bundleId,
        threshold: thresholdCredits,
      })
    },
    onSuccess: handleUpdateAutoRefillSuccess,
    onError: error => handleApiMutationError(error, 'update'),
  })

  // Mutation for disabling auto-refill
  const disableAutoRefillMutation = useMutation({
    mutationFn: async () => {
      return await api.disableAutoRefill()
    },
    onSuccess: () => handleMutationSuccess('', false, 'Auto-refill disabled successfully'),
    onError: error => {
      console.error('Failed to disable auto-refill:', error)
      // Error display is handled by component via error state, not notifications
    },
  })

  // Reset state when modal opens
  const resetModalState = useCallback(() => {
    const activePlans = plans?.filter(p => p.active) || []

    // Determine default plan: current plan if it exists and is active, otherwise cheapest active plan
    const currentPlanExists = currentPlan && activePlans.some(p => p.bundleId === currentPlan.bundleId)
    const cheapestPlan = activePlans.sort((a, b) => a.priceDollars - b.priceDollars)[0]
    const defaultPlan = currentPlanExists ? currentPlan : cheapestPlan

    setSelectedPlan(defaultPlan?.bundleId || '')
    setAutoRefillEnabled(currentAutoRefillEnabled)

    // Initialize threshold - use current value or calculate default (10% of selected plan)
    const defaultThreshold =
      currentThresholdCredits || (defaultPlan ? calculateThreshold(defaultPlan.creditsPerBundle) : 0)
    setThresholdCredits(defaultThreshold)

    // Reset payment flow state
    setShowPaymentForm(false)
    setClientSecret(null)
    setIsUpdating(false)
  }, [plans, currentPlan, currentAutoRefillEnabled, currentThresholdCredits])

  // Event handlers
  const handlePlanSelect = useCallback(
    (planId: string) => {
      setSelectedPlan(planId)
      // Update threshold to default for the new plan if not manually set
      const newPlan = plans?.find(p => p.bundleId === planId)
      if (newPlan) {
        setThresholdCredits(calculateThreshold(newPlan.creditsPerBundle))
      }
    },
    [plans],
  )

  const handleAutoRefillChange = useCallback(
    (enabled: boolean) => {
      setAutoRefillEnabled(enabled)

      // When enabling auto-refill, update threshold to match the currently selected plan
      if (enabled && selectedPlan) {
        const plan = plans?.find(p => p.bundleId === selectedPlan)
        if (plan) {
          setThresholdCredits(calculateThreshold(plan.creditsPerBundle))
        }
      }
    },
    [selectedPlan, plans],
  )

  const handleThresholdChange = useCallback((threshold: number) => {
    setThresholdCredits(threshold)
  }, [])

  const handleSave = useCallback(async () => {
    if (autoRefillEnabled) {
      // Auto-refill is being enabled - we need a valid selected plan
      const plan = plans?.find(p => p.bundleId === selectedPlan)
      if (!selectedPlan || !plan) {
        console.error('Invalid selected plan:', selectedPlan)
        return
      }

      if (hasPaymentMethod) {
        // Payment method exists, directly enable auto-refill
        updateAutoRefillMutation.mutate()
      } else {
        // No payment method, first set up payment method
        setupPaymentMethodMutation.mutate()
      }
    } else {
      disableAutoRefillMutation.mutate()
    }
  }, [
    autoRefillEnabled,
    plans,
    selectedPlan,
    hasPaymentMethod,
    updateAutoRefillMutation,
    setupPaymentMethodMutation,
    disableAutoRefillMutation,
  ])

  const handlePaymentSuccess = useCallback(async () => {
    if (!selectedPlan) return

    setIsUpdating(true)
    setIsPollingAfterPayment(true)
    try {
      // Poll for payment method update before enabling auto-refill
      const pollSuccess = await pollForPaymentMethodUpdate()

      if (pollSuccess) {
        // Payment method is now available, enable auto-refill
        const plan = plans?.find(p => p.bundleId === selectedPlan)
        if (!plan) {
          throw new Error('Plan not found after payment success')
        }

        // Call updateAutoRefill to enable auto-refill now that payment method exists
        await api.updateAutoRefill({
          bundle_id: plan.bundleId,
          threshold: thresholdCredits,
        })

        // Complete the flow
        await handleMutationSuccess(selectedPlan, autoRefillEnabled, 'Auto-refill setup completed successfully')
      } else {
        // Polling timed out, but we'll still try to enable auto-refill
        console.warn('Payment method polling timed out, attempting to enable auto-refill anyway')

        const plan = plans?.find(p => p.bundleId === selectedPlan)
        if (plan) {
          try {
            await api.updateAutoRefill({
              bundle_id: plan.bundleId,
              threshold: thresholdCredits,
            })

            await handleMutationSuccess(
              selectedPlan,
              autoRefillEnabled,
              'Auto-refill setup complete. Payment method may take a few moments to appear.',
            )
          } catch (autoRefillError) {
            console.error('Failed to enable auto-refill after payment:', autoRefillError)
            throw new Error('Payment successful but auto-refill setup failed')
          }
        }

        // Show additional notification about processing
        notifications.show({
          title: 'Setup Processing',
          message: 'Your payment method is being processed and will be available shortly.',
          color: 'blue',
        })
      }
    } catch (error) {
      console.error('Failed to handle payment success:', error)
      notifications.show({
        title: 'Setup Error',
        message: 'There was an issue completing the auto-refill setup. Please contact support if the problem persists.',
        color: 'red',
      })
    } finally {
      setIsUpdating(false)
      setIsPollingAfterPayment(false)
    }
  }, [selectedPlan, autoRefillEnabled, handleMutationSuccess, pollForPaymentMethodUpdate, plans, thresholdCredits, api])

  const handleBackFromPayment = useCallback(() => {
    setShowPaymentForm(false)
    setClientSecret(null)
  }, [])

  // Computed loading state
  const isLoading =
    isUpdating ||
    isPollingAfterPayment ||
    setupPaymentMethodMutation.isPending ||
    updateAutoRefillMutation.isPending ||
    disableAutoRefillMutation.isPending

  // Computed error state with user-friendly messages
  const getErrorMessage = () => {
    if (setupPaymentMethodMutation.error) {
      return 'Unable to set up payment method. Please check your payment details and try again.'
    }
    if (updateAutoRefillMutation.error) {
      return 'Unable to update auto-refill settings. Please verify your payment method is valid and try again.'
    }
    if (disableAutoRefillMutation.error) {
      return 'Unable to disable auto-refill. Please try again or contact support if the problem persists.'
    }
    return null
  }

  const error = getErrorMessage()

  return {
    // State
    selectedPlan,
    selectedPlanData,
    autoRefillEnabled,
    thresholdCredits,
    showPaymentForm,
    clientSecret,
    hasPaymentMethod,
    isLoading,
    error,

    // Mutations (exposed for granular error handling in components)
    setupPaymentMethodMutation,
    updateAutoRefillMutation,
    disableAutoRefillMutation,

    // Event handlers
    handlePlanSelect,
    handleAutoRefillChange,
    handleThresholdChange,
    handleSave,
    handlePaymentSuccess,
    handleBackFromPayment,
    resetModalState,
  }
}
